/**
 * Volver a entrar sin correo: QR del centro + PIN (hermano de /login para
 * quien no tiene contraseña).
 *
 * `QrScanner` es una pantalla propia a toda página (no acepta título, ayuda
 * ni error: los suyos son fijos), así que el mensaje de error se enseña
 * ANTES de abrirla, en esta misma tarjeta — no dentro del escáner. El QR
 * caduca a los 30 s: si `recuperarAcceso` falla, se vuelve aquí y se pide
 * escanear otra vez, nunca reintentar con el mismo token.
 *
 * Cuando el backend responde con un mensaje (`apiError.message`), se enseña
 * TAL CUAL y no se resume. "Ese PIN lo tienen dos personas" y "Demasiados
 * intentos, espera 15 minutos" son cosas distintas: la segunda se arregla
 * esperando, la primera sólo la arregla el responsable. Un "no se pudo
 * entrar" genérico deja al trabajador parado en la obra sin saber a quién
 * llamar. El genérico sólo entra cuando NO hay mensaje del backend —sin
 * respuesta, sin conexión— porque ahí no hay nada que citar.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentUser, recuperarAcceso } from '../../services/apiClient';
import { useAppStore } from '../../store/useAppStore';
import { syncService } from '../../services/syncService';
import { QrScanner } from '../QrScanner';
import { AuthLayout, CARD_SHADOW } from './AuthLayout';
import { Icon } from '../ui/Icon';

type Paso = 'intro' | 'escaneando' | 'pin';

export function RecuperarAccesoPage() {
  const [paso, setPaso] = useState<Paso>('intro');
  const [centro, setCentro] = useState<{ worksiteUuid: string; qrToken: string } | null>(null);
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { setUser } = useAppStore();
  const navigate = useNavigate();

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!centro) return;
    setBusy(true);
    setError(null);
    try {
      await recuperarAcceso(centro.worksiteUuid, centro.qrToken, pin);
      const user = await getCurrentUser();
      setUser({
        id: user.id,
        email: user.email,
        firstName: user.first_name,
        lastName: user.last_name,
        role: user.role as 'ADMIN' | 'MANAGER' | 'EMPLOYEE',
        tenantId: user.tenant_id,
      });
      syncService.start(user.id);
      navigate('/');
    } catch (err: unknown) {
      // Tal cual del backend, sin resumir (ver docstring). El QR ya ha
      // caducado o está a punto: se vuelve a la intro para escanear de
      // nuevo, no se reintenta con el mismo token.
      const apiError = (err as { apiError?: { message?: string } })?.apiError;
      setError(apiError?.message ?? 'No se ha podido recuperar el acceso. Vuelve a escanear.');
      setPin('');
      setCentro(null);
      setPaso('intro');
    } finally {
      setBusy(false);
    }
  };

  if (paso === 'escaneando') {
    return (
      <QrScanner
        onCancel={() => setPaso('intro')}
        onScan={(worksiteUuid, qrToken) => {
          if (!qrToken) {
            // Cartel impreso sin token dinámico: no vale para recuperar el
            // acceso (sólo prueba el centro, no que quien mira la pantalla
            // está delante). Y no siempre hay pantalla: `require_dynamic_qr`
            // va a False por defecto, así que hay centros donde el cartel es
            // lo único que existe. Decir sólo "escanea la pantalla" deja
            // parado a quien no la tiene: el mensaje nombra a quien puede
            // resolverlo.
            setError('Ese código no sirve para entrar. Necesitas el QR de acceso que cambia cada pocos segundos en la pantalla del centro: si sólo tienes el cartel impreso, pídeselo a tu responsable.');
            setPaso('intro');
            return;
          }
          setCentro({ worksiteUuid, qrToken });
          setPaso('pin');
        }}
      />
    );
  }

  if (paso === 'pin') {
    return (
      <AuthLayout>
        <div
          className="w-full max-w-[440px] self-start bg-canvas border border-line-strong rounded-3xl p-7 sm:p-11 animate-rise"
          style={CARD_SHADOW}
        >
          <button
            onClick={() => {
              setCentro(null);
              setPaso('intro');
            }}
            className="inline-flex items-center gap-1.5 text-ink-2 no-underline text-[14px] font-semibold mb-5"
          >
            <Icon name="chevron-left" size={17} strokeWidth={2} />
            Volver
          </button>
          <h1 className="font-display font-extrabold text-[26px] sm:text-[32px] tracking-tight mb-2.5">
            Ahora tu PIN
          </h1>
          <p className="text-ink-2 text-[15.5px] leading-relaxed mb-6">
            Centro reconocido. Escribe el PIN de cuatro cifras que te enseñó Kakuat al activar
            tu cuenta.
          </p>
          <form onSubmit={entrar} className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5">
              <span className="label !mb-0">PIN</span>
              {/* Enmascarado como en el quiosco: esta pantalla se usa de pie en
                  una obra o en la barra de un bar, con gente al lado, y el PIN
                  es la única credencial de esa persona. Sin botón de
                  ver/ocultar porque el quiosco tampoco lo ofrece. */}
              <input
                type="password"
                inputMode="numeric"
                autoFocus
                autoComplete="off"
                minLength={4}
                maxLength={6}
                required
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                className="field bg-surface text-center text-[28px] tracking-[0.4em] font-mono"
              />
            </label>
            <button type="submit" disabled={busy || pin.length < 4} className="btn-primary">
              {busy ? 'Entrando…' : 'Entrar'}
            </button>
          </form>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div
        className="w-full max-w-[440px] self-start bg-canvas border border-line-strong rounded-3xl p-7 sm:p-11 animate-rise"
        style={CARD_SHADOW}
      >
        <Link to="/login" className="inline-flex items-center gap-1.5 text-ink-2 no-underline text-[14px] font-semibold mb-5">
          <Icon name="chevron-left" size={17} strokeWidth={2} />
          Volver
        </Link>
        <h1 className="font-display font-extrabold text-[26px] sm:text-[32px] tracking-tight mb-2.5">
          Recupera el acceso
        </h1>
        <p className="text-ink-2 text-[15.5px] leading-relaxed mb-6">
          Sin correo, entras escaneando el código del centro y tecleando tu PIN. Es el cartel de
          la entrada: cambia cada 30 segundos para que una foto no valga.
        </p>

        {error && (
          <p className="rounded-xl bg-danger-soft text-danger px-4 py-3 text-[14px] mb-4">{error}</p>
        )}

        <button onClick={() => setPaso('escaneando')} className="btn-primary">
          Escanear el código del centro
        </button>
      </div>
    </AuthLayout>
  );
}
